import { inject, Injectable, signal } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import {
  AdminDeletedUserListItemDto,
  AdminUserManagementDetailResponse,
  AdminUsersManagementListResponse,
  AdminUsersManagementService,
  DeleteUserEligibilityResponseDto,
  ListAdminDeletedUsersResponse,
  ListDeletedUsersRequestParams,
  ListRequestParams,
  UpdateAdminUserManagementDto,
} from '@smatchy/sdk';
import { UserDetail } from '../../presentations/components/users-management-detail/users-management-detail.component';
import { sanitizeUserAvatarUrl } from '../../presentations/pages/users-management/users-management-avatar';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AccountSuspensionReason } from '../../presentations/utils/an-account-suspension';

export type UsersManagementListResponse = AdminUsersManagementListResponse;

export type UsersManagementDetailResponse = AdminUserManagementDetailResponse;

export type UpdateUserManagementPayload = UpdateAdminUserManagementDto;

export type UsersManagementListSortBy = NonNullable<ListRequestParams['sortBy']>;

export type UsersManagementListFilters = {
  search?: string;
  userType?: string;
  status?: string;
  pushNotification?: boolean;
  createdFrom?: string | null;
  createdTo?: string | null;
  loginFrom?: string | null;
  loginTo?: string | null;
  /** Exact city name, matched case-insensitively by the API. */
  city?: string;
  /** Profile completion bounds, both inclusive, as percentages. */
  completionMin?: number;
  completionMax?: number;
  sortBy?: UsersManagementListSortBy;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
};

export type DeletedUsersListFilters = ListDeletedUsersRequestParams & {
  userType?: string;
  status?: string;
  createdFrom?: string | null;
  createdTo?: string | null;
  loginFrom?: string | null;
  loginTo?: string | null;
};

export type DeletedUsersServerFilters = Omit<DeletedUsersListFilters, 'page' | 'pageSize'>;

@Injectable({ providedIn: 'root' })
export class UsersManagementStoreService {
  #adminUsersApi = inject(AdminUsersManagementService);
  #http = inject(HttpClient);

  loading = signal(false);
  detailLoading = signal(false);
  users = signal<UserDetail[]>([]);
  totalUsers = signal(0);
  proUsersCount = signal(0);
  error = signal<string | null>(null);

  /**
   * Opens the CSV the API builds, carrying the filters currently applied.
   *
   * Goes through HttpClient and a blob rather than pointing the browser at the
   * URL: the endpoint needs the bearer token, which a plain navigation would
   * not send.
   */
  exportUsersCsv(filters: UsersManagementListFilters = {}): Observable<Blob> {
    const params = new HttpParams({
      fromObject: Object.entries(this.toListParams(filters)).reduce<Record<string, string>>((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = String(value);
        }
        return acc;
      }, {}),
    });

    return this.#http.get(`${environment.api}/api/v1/admin/users-management/export/csv`, {
      params,
      responseType: 'blob',
    });
  }

  loadUsers(filters: UsersManagementListFilters = {}): void {
    this.loading.set(true);
    this.error.set(null);

    this.#adminUsersApi.list(this.toListParams(filters)).subscribe({
      next: (res) => {
        const body = this.unwrapValue(res);
        this.loading.set(false);
        this.users.set((body?.users ?? []).map((u) => this.normalizeUser(u)));
        this.totalUsers.set(body?.totalUsers ?? 0);
        this.proUsersCount.set(body?.proUsersCount ?? 0);
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Failed to load users');
        this.users.set([]);
        this.totalUsers.set(0);
        this.proUsersCount.set(0);
      },
    });
  }

  loadUserDetail(userId: string) {
    return this.#adminUsersApi.getDetail({ userId }).pipe(map((res) => this.unwrapValue(res)));
  }

  updateUser(userId: string, payload: UpdateUserManagementPayload) {
    return this.#adminUsersApi
      .update({ userId, updateAdminUserManagementDto: payload })
      .pipe(map((res) => this.unwrapValue(res)));
  }

  getDeleteEligibility(userId: string) {
    return this.#adminUsersApi
      .getDeleteEligibility({ userId })
      .pipe(map((res) => this.unwrapValue(res) as DeleteUserEligibilityResponseDto));
  }

  deleteUser(userId: string) {
    return this.#adminUsersApi._delete({ userId });
  }

  // Called over HttpClient rather than the generated SDK: these routes are
  // newer than its last regeneration (see an-account-suspension.ts).
  suspendAccount(userId: string, reason: AccountSuspensionReason) {
    return this.#http.post<void>(
      `${environment.api}/api/v1/admin/users-management/${encodeURIComponent(userId)}/suspension`,
      { reason },
    );
  }

  liftSuspension(userId: string) {
    return this.#http.delete<void>(
      `${environment.api}/api/v1/admin/users-management/${encodeURIComponent(userId)}/suspension`,
    );
  }

  listDeletedUsers(filters: DeletedUsersListFilters = {}): Observable<ListAdminDeletedUsersResponse> {
    return this.#adminUsersApi
      .listDeletedUsers(this.toDeletedUsersParams(filters))
      .pipe(map((res) => this.unwrapValue(res)));
  }

  listAllDeletedUsers(filters: DeletedUsersServerFilters = {}): Observable<AdminDeletedUserListItemDto[]> {
    const pageSize = 100;
    return this.listDeletedUsers({ ...filters, page: 1, pageSize }).pipe(
      switchMap((firstPage) => {
        const total = firstPage.totalUsers ?? 0;
        const totalPages = Math.max(1, Math.ceil(total / pageSize));
        const users = [...(firstPage.users ?? [])];
        if (totalPages <= 1) {
          return of(users);
        }
        const otherPages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
        return forkJoin(
          otherPages.map((page) =>
            this.listDeletedUsers({ ...filters, page, pageSize }).pipe(map((res) => res.users ?? [])),
          ),
        ).pipe(map((pages) => users.concat(...pages)));
      }),
    );
  }

  normalizeUser(user: UserDetail): UserDetail {
    return {
      ...user,
      id: user.id as string | number,
      avatar: sanitizeUserAvatarUrl(user.avatar),
      role: user.userType?.label ?? user.role ?? '',
      sports: user.sports?.map((s) => ({ ...s, sportId: s.sportId ?? '' })),
      createdAt: user.createdAt ? new Date(user.createdAt).toISOString() : user.createdAt,
    };
  }

  private unwrapValue<T>(res: { value?: T } | T): T {
    const body = res as { value?: T };
    return (body.value ?? res) as T;
  }

  /** Drops anything unset, so an untouched filter never narrows the list. */
  private toListParams(filters: UsersManagementListFilters): ListRequestParams {
    return {
      search: filters.search?.trim() || undefined,
      userType: filters.userType as ListRequestParams['userType'],
      status: filters.status as ListRequestParams['status'],
      pushNotification: filters.pushNotification,
      createdFrom: filters.createdFrom ?? undefined,
      createdTo: filters.createdTo ?? undefined,
      loginFrom: filters.loginFrom ?? undefined,
      loginTo: filters.loginTo ?? undefined,
      city: filters.city?.trim() || undefined,
      completionMin: filters.completionMin ?? undefined,
      completionMax: filters.completionMax ?? undefined,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
      page: filters.page,
      pageSize: filters.pageSize,
    };
  }

  private toDeletedUsersParams(filters: DeletedUsersListFilters): ListDeletedUsersRequestParams {
    return {
      search: filters.search?.trim() || undefined,
      userType: filters.userType as ListDeletedUsersRequestParams['userType'],
      status: filters.status as ListDeletedUsersRequestParams['status'],
      pushNotification: filters.pushNotification,
      createdFrom: filters.createdFrom ?? undefined,
      createdTo: filters.createdTo ?? undefined,
      loginFrom: filters.loginFrom ?? undefined,
      loginTo: filters.loginTo ?? undefined,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
      page: filters.page,
      pageSize: filters.pageSize,
      deletedFrom: filters.deletedFrom,
      deletedTo: filters.deletedTo,
      deletedById: filters.deletedById,
    };
  }
}
